import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check, Copy, X } from 'lucide-react';
import { showErrorToast } from '../../lib/toast';
import { cn } from '../../lib/utils';
import {
  WIDGET_LAYOUTS,
  installGuides,
  normalizeLayout,
  widgetScriptAttributes,
} from './widgetSnippet';

const WIDGET_NODE_IDS = ['manteo-chat-frame', 'manteo-chat-backdrop', 'manteo-chat-button'];

const removeWidgetNodes = () => {
  WIDGET_NODE_IDS.forEach((id) => {
    document.getElementById(id)?.remove();
  });
};

const applyScriptAttributes = (script, publicKey, layout) => {
  Object.entries(widgetScriptAttributes({ publicKey, layout })).forEach(([name, value]) => {
    script.setAttribute(name, value);
  });
};

const WidgetPreview = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const publicKey = searchParams.get('key') || '';
  const layout = normalizeLayout(searchParams.get('layout'));
  const layoutRef = useRef(layout);
  layoutRef.current = layout;
  const [language, setLanguage] = useState('html');
  const [copied, setCopied] = useState(false);
  const [hintDismissed, setHintDismissed] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  const guides = installGuides({
    origin: window.location.origin,
    publicKey,
    layout,
  });
  const activeGuide = guides.find(guide => guide.id === language) || guides[0];
  const selectedLayout = WIDGET_LAYOUTS.find(item => item.id === layout);

  useEffect(() => {
    document.title = 'manteo.ai | Widget preview';
  }, []);

  useEffect(() => {
    if (!publicKey) return undefined;
    const generation = `${Date.now()}-${Math.random()}`;
    window.__manteoPreviewGeneration = generation;
    const script = document.createElement('script');
    script.id = 'manteo-preview-script';
    script.src = `${window.location.origin}/embed.js`;
    script.async = true;
    script.setAttribute('data-generation', generation);
    applyScriptAttributes(script, publicKey, layoutRef.current);
    document.body.appendChild(script);
    return () => {
      window.__manteoPreviewGeneration = 'closed';
      script.remove();
      removeWidgetNodes();
      delete window.manteoPreviewRemount;
    };
  }, [publicKey]);

  useEffect(() => {
    if (!publicKey) return undefined;
    const script = document.getElementById('manteo-preview-script');
    if (!script) return undefined;
    applyScriptAttributes(script, publicKey, layout);
    window.manteoPreviewRemount?.();
    return undefined;
  }, [publicKey, layout]);

  useEffect(() => {
    if (!publicKey) return undefined;
    let observer;
    let watched = null;
    const sync = () => {
      const button = document.getElementById('manteo-chat-button');
      setChatOpen(Boolean(button) && button.style.visibility === 'hidden');
      if (!button || button === watched) return;
      observer?.disconnect();
      watched = button;
      observer = new MutationObserver(sync);
      observer.observe(button, { attributes: true, attributeFilter: ['style'] });
    };
    const timer = window.setInterval(sync, 200);
    sync();
    return () => {
      window.clearInterval(timer);
      observer?.disconnect();
    };
  }, [publicKey, layout]);

  const selectLayout = (next) => {
    const params = new URLSearchParams(searchParams);
    params.set('layout', next);
    setSearchParams(params, { replace: true });
    setCopied(false);
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(activeGuide.code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Failed to copy embed code:', err);
      showErrorToast('Could not copy the embed code.');
    }
  };

  if (!publicKey) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-stone-100 px-6 text-gray-900">
        <p className="max-w-md text-center text-sm text-gray-600">
          This preview needs a widget key. Open it from the Widgets page.
        </p>
      </div>
    );
  }

  const showHint = !hintDismissed && !chatOpen;

  return (
    <div className="min-h-screen bg-stone-100 text-gray-900">
      <div className="mx-auto max-w-3xl px-5 py-8 pb-40">
        <header>
          <p className="text-[10px] font-black uppercase tracking-widest text-emerald-700">Sample page</p>
          <h1 className="mt-2 text-3xl font-black tracking-tight text-gray-950">Widget preview</h1>
          <p className="mt-2 max-w-xl text-sm text-gray-600">
            This is how the chat sits on your site. The round button in the bottom-right corner is the widget. Click it to open the chat.
          </p>
        </header>

        <section className="mt-6" aria-label="Layout">
          <h2 className="text-[10px] font-black uppercase tracking-widest text-gray-500">Layout</h2>
          <div className="mt-2 flex flex-wrap gap-2">
            {WIDGET_LAYOUTS.map(item => (
              <button
                key={item.id}
                type="button"
                aria-pressed={item.id === layout}
                onClick={() => selectLayout(item.id)}
                className={cn(
                  'rounded-full px-3 py-1.5 text-xs font-semibold',
                  item.id === layout
                    ? 'bg-gray-950 text-white'
                    : 'bg-white text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50'
                )}
              >
                {item.label}
              </button>
            ))}
          </div>
          <p className="mt-2 text-sm text-gray-600">{selectedLayout?.description}</p>
        </section>

        <section className="mt-8 rounded-3xl border border-stone-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-base font-black text-gray-950">Embed code</h2>
              <p className="mt-1 text-sm text-gray-600">
                Pick the stack you use. The snippet includes the layout selected above. You can still change color, welcome message, and questions on the script tag.
              </p>
            </div>
            <button
              type="button"
              onClick={copyCode}
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>

          <div className="mt-4 flex flex-wrap gap-1" role="tablist" aria-label="Install guides">
            {guides.map(guide => (
              <button
                key={guide.id}
                type="button"
                role="tab"
                aria-selected={guide.id === activeGuide.id}
                onClick={() => {
                  setLanguage(guide.id);
                  setCopied(false);
                }}
                className={cn(
                  'rounded-lg px-3 py-1.5 text-xs font-semibold',
                  guide.id === activeGuide.id
                    ? 'bg-emerald-50 text-emerald-800'
                    : 'text-gray-500 hover:bg-gray-50 hover:text-gray-800'
                )}
              >
                {guide.label}
              </button>
            ))}
          </div>
          <p className="mt-3 text-sm text-gray-700">{activeGuide.guide}</p>
          <pre
            data-testid="embed-code"
            className="mt-3 max-h-80 overflow-auto rounded-2xl bg-gray-950 p-4 text-xs leading-relaxed text-gray-100"
          >
            <code>{activeGuide.code}</code>
          </pre>
        </section>
      </div>

      {showHint && (
        <div className="fixed bottom-24 right-4 z-[2147483002] w-64 max-w-[calc(100vw-2rem)]">
          <div className="relative rounded-2xl bg-gray-950 px-4 py-3 text-white shadow-2xl">
            <button
              type="button"
              onClick={() => setHintDismissed(true)}
              className="absolute right-2 top-2 rounded-md p-1 text-gray-400 hover:bg-white/10 hover:text-white"
              aria-label="Dismiss widget hint"
            >
              <X size={14} />
            </button>
            <p className="pr-5 text-sm font-semibold">Your widget is here</p>
            <p className="mt-1 text-xs leading-relaxed text-gray-300">
              The round button below opens the chat. It stays in this corner for every layout.
            </p>
            <span
              className="absolute -bottom-2 right-6 h-0 w-0 border-x-8 border-t-8 border-x-transparent border-t-gray-950"
              aria-hidden="true"
            />
          </div>
        </div>
      )}
      {showHint && (
        <div
          className="pointer-events-none fixed bottom-3 right-3 z-[2147483002] h-[68px] w-[68px] animate-pulse rounded-full border-2 border-emerald-500"
          aria-hidden="true"
        />
      )}
    </div>
  );
};

export default WidgetPreview;
